import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  Search, 
  FileText, 
  Download, 
  ExternalLink, 
  History, 
  Star, 
  StarOff,
  Mail, 
  Trash2, 
  FileDown,
  ChevronDown,
  X,
  Loader2,
  ShieldCheck,
  FileSearch
} from "lucide-react";

// Shadcn UI Components
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./components/ui/dialog";
import { ScrollArea } from "./components/ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./components/ui/tooltip";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_lab-doc-lookup/artifacts/r6rcd6s3_images%20%281%29.png";

// Suppliers (sorted alphabetically A-Z)
const SUPPLIERS = [
  { id: "hardy_diagnostics", name: "Hardy Diagnostics" },
  { id: "labchem", name: "LabChem" },
  { id: "macherey_nagel", name: "Macherey-Nagel" },
  { id: "neogen", name: "Neogen" },
  { id: "prognosis_biotech", name: "Prognosis Biotech" },
  { id: "reagents", name: "Reagents" },
  { id: "ricca_chemicals", name: "Ricca Chemicals" },
  { id: "spectrum_chemicals", name: "Spectrum Chemicals" },
  { id: "tedia", name: "Tedia" },
  { id: "vwr", name: "VWR" },
];

// SDS Suppliers (subset - excludes suppliers that don't retrieve SDS info)
const SDS_SUPPLIERS = [
  { id: "hardy_diagnostics", name: "Hardy Diagnostics" },
  { id: "labchem", name: "LabChem" },
  { id: "macherey_nagel", name: "Macherey-Nagel" },
  { id: "reagents", name: "Reagents" },
  { id: "ricca_chemicals", name: "Ricca Chemicals" },
  { id: "spectrum_chemicals", name: "Spectrum Chemicals" },
];

// Local Storage Keys
const COA_HISTORY_KEY = "coa_search_history";
const SDS_HISTORY_KEY = "sds_search_history";
const FAVORITES_KEY = "coa_favorites";

// Suppliers with aggressive bot protection (known to always block server-side proxy)
const NO_PREVIEW_SUPPLIERS = ["spectrum_chemicals"];

// Get the appropriate PDF viewer URL for an iframe
const getPdfViewerUrl = (pdfUrl, supplier) => {
  if (NO_PREVIEW_SUPPLIERS.includes(supplier)) {
    return null; // Can't preview these - will show fallback UI
  }
  return `${API}/coa/proxy?url=${encodeURIComponent(pdfUrl)}`;
};

function App() {
  // COA Form state
  const [coaSku, setCoaSku] = useState("");
  const [coaLotNumber, setCoaLotNumber] = useState("");
  const [coaSupplier, setCoaSupplier] = useState("");
  const [isCoaSearching, setIsCoaSearching] = useState(false);
  const [coaResult, setCoaResult] = useState(null);
  const [isCoaPdfLoading, setIsCoaPdfLoading] = useState(false);

  // SDS Form state
  const [sdsSku, setSdsSku] = useState("");
  const [sdsSupplier, setSdsSupplier] = useState("");
  const [isSdsSearching, setIsSdsSearching] = useState(false);
  const [sdsResult, setSdsResult] = useState(null);
  const [isSdsPdfLoading, setIsSdsPdfLoading] = useState(false);

  // History state
  const [coaHistory, setCoaHistory] = useState([]);
  const [sdsHistory, setSdsHistory] = useState([]);
  const [favorites, setFavorites] = useState([]);

  // Email dialog state
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailAddress, setEmailAddress] = useState("");
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailTarget, setEmailTarget] = useState(null); // { type: 'coa' | 'sds', data: result }

  // Active tab for results
  const [activeResultTab, setActiveResultTab] = useState("coa"); // 'coa' or 'sds'

  // Detect if running inside an iframe (embedded mode)
  const [isEmbedded, setIsEmbedded] = useState(false);
  useEffect(() => {
    try {
      setIsEmbedded(window.self !== window.top);
    } catch (e) {
      setIsEmbedded(true);
    }
  }, []);

  // Load history and favorites from localStorage
  useEffect(() => {
    const savedCoaHistory = localStorage.getItem(COA_HISTORY_KEY);
    const savedSdsHistory = localStorage.getItem(SDS_HISTORY_KEY);
    const savedFavorites = localStorage.getItem(FAVORITES_KEY);
    
    if (savedCoaHistory) {
      try { setCoaHistory(JSON.parse(savedCoaHistory)); } catch (e) {}
    }
    if (savedSdsHistory) {
      try { setSdsHistory(JSON.parse(savedSdsHistory)); } catch (e) {}
    }
    if (savedFavorites) {
      try { setFavorites(JSON.parse(savedFavorites)); } catch (e) {}
    }
  }, []);

  // Save COA history
  const saveCoaHistory = useCallback((history) => {
    localStorage.setItem(COA_HISTORY_KEY, JSON.stringify(history));
    setCoaHistory(history);
  }, []);

  // Save SDS history
  const saveSdsHistory = useCallback((history) => {
    localStorage.setItem(SDS_HISTORY_KEY, JSON.stringify(history));
    setSdsHistory(history);
  }, []);

  // Save favorites
  const saveFavorites = useCallback((favs) => {
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(favs));
    setFavorites(favs);
  }, []);

  // Add to COA history
  const addToCoaHistory = useCallback((result) => {
    const newEntry = {
      id: Date.now().toString(),
      type: "coa",
      sku: result.sku,
      lot_number: result.lot_number,
      source: result.source,
      source_name: result.source_name,
      coa_url: result.coa_url,
      timestamp: new Date().toISOString(),
    };

    setCoaHistory((prev) => {
      const filtered = prev.filter(
        (h) => !(h.sku === newEntry.sku && h.lot_number === newEntry.lot_number && h.source === newEntry.source)
      );
      const newHistory = [newEntry, ...filtered].slice(0, 30);
      localStorage.setItem(COA_HISTORY_KEY, JSON.stringify(newHistory));
      return newHistory;
    });
  }, []);

  // Add to SDS history
  const addToSdsHistory = useCallback((result) => {
    const newEntry = {
      id: Date.now().toString(),
      type: "sds",
      sku: result.sku,
      source: result.source,
      source_name: result.source_name,
      sds_url: result.sds_url,
      timestamp: new Date().toISOString(),
    };

    setSdsHistory((prev) => {
      const filtered = prev.filter(
        (h) => !(h.sku === newEntry.sku && h.source === newEntry.source)
      );
      const newHistory = [newEntry, ...filtered].slice(0, 30);
      localStorage.setItem(SDS_HISTORY_KEY, JSON.stringify(newHistory));
      return newHistory;
    });
  }, []);

  // Search COA
  const handleCoaSearch = async (e) => {
    e?.preventDefault();
    
    if (!coaSku.trim()) {
      toast.error("Please enter a SKU/Catalog Number");
      return;
    }
    if (!coaLotNumber.trim()) {
      toast.error("Please enter a Lot Number");
      return;
    }
    if (!coaSupplier) {
      toast.error("Please select a supplier");
      return;
    }

    setIsCoaSearching(true);
    setCoaResult(null);
    setIsCoaPdfLoading(true);

    try {
      const response = await axios.post(`${API}/coa/search`, {
        sku: coaSku.trim(),
        lot_number: coaLotNumber.trim(),
        source: coaSupplier,
      });

      if (response.data.success) {
        setCoaResult(response.data);
        addToCoaHistory(response.data);
        setActiveResultTab("coa");
        
        if (response.data.requires_manual_lookup) {
          toast.info("COA requires manual lookup. Click 'Open Search' to find it.");
        } else {
          toast.success("COA found successfully!");
        }
      } else {
        toast.error(response.data.error || "Failed to find COA");
      }
    } catch (error) {
      console.error("COA Search error:", error);
      toast.error("Error searching for COA. Please try again.");
    } finally {
      setIsCoaSearching(false);
    }
  };

  // Search SDS
  const handleSdsSearch = async (e) => {
    e?.preventDefault();
    
    if (!sdsSku.trim()) {
      toast.error("Please enter a SKU/Catalog Number");
      return;
    }
    if (!sdsSupplier) {
      toast.error("Please select a supplier");
      return;
    }

    setIsSdsSearching(true);
    setSdsResult(null);
    setIsSdsPdfLoading(true);

    try {
      const response = await axios.post(`${API}/sds/search`, {
        sku: sdsSku.trim(),
        source: sdsSupplier,
      });

      if (response.data.success) {
        setSdsResult(response.data);
        addToSdsHistory(response.data);
        setActiveResultTab("sds");
        
        if (response.data.requires_manual_lookup) {
          toast.info("SDS requires manual lookup. Click 'Open Search' to find it.");
        } else {
          toast.success("SDS found successfully!");
        }
      } else {
        toast.error(response.data.error || "Failed to find SDS");
      }
    } catch (error) {
      console.error("SDS Search error:", error);
      toast.error("Error searching for SDS. Please try again.");
    } finally {
      setIsSdsSearching(false);
    }
  };

  // Load from COA history
  const loadFromCoaHistory = (item) => {
    setCoaSku(item.sku);
    setCoaLotNumber(item.lot_number);
    setCoaSupplier(item.source);
    setCoaResult({
      success: true,
      sku: item.sku,
      lot_number: item.lot_number,
      source: item.source,
      source_name: item.source_name,
      coa_url: item.coa_url,
    });
    setIsCoaPdfLoading(true);
    setActiveResultTab("coa");
    toast.info("Loaded from COA history");
  };

  // Load from SDS history
  const loadFromSdsHistory = (item) => {
    setSdsSku(item.sku);
    setSdsSupplier(item.source);
    setSdsResult({
      success: true,
      sku: item.sku,
      source: item.source,
      source_name: item.source_name,
      sds_url: item.sds_url,
    });
    setIsSdsPdfLoading(true);
    setActiveResultTab("sds");
    toast.info("Loaded from SDS history");
  };

  // Toggle favorite (COA only)
  const toggleFavorite = (item) => {
    const itemKey = `${item.sku}-${item.lot_number}-${item.source}`;
    const isFav = favorites.some(
      (f) => `${f.sku}-${f.lot_number}-${f.source}` === itemKey
    );

    if (isFav) {
      const newFavorites = favorites.filter(
        (f) => `${f.sku}-${f.lot_number}-${f.source}` !== itemKey
      );
      saveFavorites(newFavorites);
      toast.success("Removed from favorites");
    } else {
      const newFavorites = [item, ...favorites];
      saveFavorites(newFavorites);
      toast.success("Added to favorites");
    }
  };

  // Check if item is favorite
  const isFavorite = (item) => {
    return favorites.some(
      (f) => `${f.sku}-${f.lot_number}-${f.source}` === `${item.sku}-${item.lot_number}-${item.source}`
    );
  };

  // Clear histories
  const clearCoaHistory = () => {
    saveCoaHistory([]);
    toast.success("COA history cleared");
  };

  const clearSdsHistory = () => {
    saveSdsHistory([]);
    toast.success("SDS history cleared");
  };

  // Delete single history item
  const deleteCoaHistoryItem = (id) => {
    const newHistory = coaHistory.filter((h) => h.id !== id);
    saveCoaHistory(newHistory);
  };

  const deleteSdsHistoryItem = (id) => {
    const newHistory = sdsHistory.filter((h) => h.id !== id);
    saveSdsHistory(newHistory);
  };

  // Export history to CSV
  const exportCoaToCSV = () => {
    if (coaHistory.length === 0) {
      toast.error("No COA history to export");
      return;
    }

    const headers = ["SKU", "Lot Number", "Supplier", "COA URL", "Date"];
    const rows = coaHistory.map((h) => [
      h.sku,
      h.lot_number,
      h.source_name,
      h.coa_url,
      new Date(h.timestamp).toLocaleString(),
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((r) => r.map((c) => `"${c}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `coa_history_${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success("COA history exported to CSV");
  };

  const exportSdsToCSV = () => {
    if (sdsHistory.length === 0) {
      toast.error("No SDS history to export");
      return;
    }

    const headers = ["SKU", "Supplier", "SDS URL", "Date"];
    const rows = sdsHistory.map((h) => [
      h.sku,
      h.source_name,
      h.sds_url,
      new Date(h.timestamp).toLocaleString(),
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((r) => r.map((c) => `"${c}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sds_history_${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success("SDS history exported to CSV");
  };

  // Send email
  const handleSendEmail = async () => {
    if (!emailAddress.trim()) {
      toast.error("Please enter an email address");
      return;
    }

    if (!emailTarget) {
      toast.error("No document to send");
      return;
    }

    setIsSendingEmail(true);

    try {
      await axios.post(`${API}/coa/email`, {
        email: emailAddress.trim(),
        sku: emailTarget.data.sku,
        lot_number: emailTarget.data.lot_number || "",
        source_name: emailTarget.data.source_name,
        coa_url: emailTarget.type === "coa" ? emailTarget.data.coa_url : emailTarget.data.sds_url,
      });

      toast.success(`Link sent to ${emailAddress}`);
      setEmailDialogOpen(false);
      setEmailAddress("");
      setEmailTarget(null);
    } catch (error) {
      console.error("Email error:", error);
      toast.error("Failed to send email. Please try again.");
    } finally {
      setIsSendingEmail(false);
    }
  };

  // Open email dialog
  const openEmailDialog = (type, data) => {
    setEmailTarget({ type, data });
    setEmailDialogOpen(true);
  };

  return (
    <TooltipProvider>
      <div className="app-container" data-testid="app-container">
        <Toaster position="top-right" richColors />
        
        {/* Header - hidden when embedded in iframe */}
        {!isEmbedded && (
          <header className="app-header" data-testid="app-header">
            <div className="header-content">
              <img 
                src={LOGO_URL} 
                alt="The Lab Depot" 
                className="header-logo"
                data-testid="header-logo"
              />
              <div className="header-title">
                <h1>The Lab Depot</h1>
                <p className="header-subtitle">Document Lookup Portal</p>
              </div>
            </div>
          </header>
        )}

        {/* Main Content */}
        <main className="main-content">
          {/* Search Forms - Side by Side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            {/* COA Search Card */}
            <Card className="search-card border-2 border-[#4A7C9B]" data-testid="coa-search-card">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-[#4A7C9B]" />
                  <CardTitle className="text-lg font-semibold text-[#1E293B]">
                    Certificate of Analysis (COA)
                  </CardTitle>
                </div>
                <CardDescription className="text-[#64748B] text-sm">
                  Search for COA by SKU, Lot Number, and Supplier
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCoaSearch}>
                  <div className="space-y-3">
                    <div className="form-group">
                      <Label htmlFor="coa-supplier" className="form-label text-sm">
                        Supplier
                      </Label>
                      <Select value={coaSupplier} onValueChange={setCoaSupplier}>
                        <SelectTrigger id="coa-supplier" className="h-9" data-testid="coa-supplier-select">
                          <SelectValue placeholder="Select a supplier..." />
                        </SelectTrigger>
                        <SelectContent>
                          {SUPPLIERS.map((s) => (
                            <SelectItem key={s.id} value={s.id} data-testid={`coa-supplier-option-${s.id}`}>
                              {s.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="form-group">
                      <Label htmlFor="coa-sku" className="form-label text-sm">
                        SKU / Catalog Number
                      </Label>
                      <Input
                        id="coa-sku"
                        type="text"
                        placeholder="e.g., A1195"
                        value={coaSku}
                        onChange={(e) => setCoaSku(e.target.value)}
                        className="h-9"
                        data-testid="coa-sku-input"
                      />
                    </div>
                    <div className="form-group">
                      <Label htmlFor="coa-lot" className="form-label text-sm">
                        Lot Number
                      </Label>
                      <Input
                        id="coa-lot"
                        type="text"
                        placeholder="e.g., 2OB0157"
                        value={coaLotNumber}
                        onChange={(e) => setCoaLotNumber(e.target.value)}
                        className="h-9"
                        data-testid="coa-lot-input"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full h-10 bg-[#4A7C9B] hover:bg-[#3D6680] text-white font-medium"
                      disabled={isCoaSearching}
                      data-testid="coa-search-button"
                    >
                      {isCoaSearching ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Searching...
                        </>
                      ) : (
                        <>
                          <Search className="mr-2 h-4 w-4" />
                          Search COA
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* SDS Search Card */}
            <Card className="search-card border-2 border-[#10B981]" data-testid="sds-search-card">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5 text-[#10B981]" />
                  <CardTitle className="text-lg font-semibold text-[#1E293B]">
                    Safety Data Sheet (SDS)
                  </CardTitle>
                </div>
                <CardDescription className="text-[#64748B] text-sm">
                  Search for SDS by SKU and Supplier
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSdsSearch}>
                  <div className="space-y-3">
                    <div className="form-group">
                      <Label htmlFor="sds-supplier" className="form-label text-sm">
                        Supplier
                      </Label>
                      <Select value={sdsSupplier} onValueChange={setSdsSupplier}>
                        <SelectTrigger id="sds-supplier" className="h-9" data-testid="sds-supplier-select">
                          <SelectValue placeholder="Select a supplier..." />
                        </SelectTrigger>
                        <SelectContent>
                          {SDS_SUPPLIERS.map((s) => (
                            <SelectItem key={s.id} value={s.id} data-testid={`sds-supplier-option-${s.id}`}>
                              {s.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="form-group">
                      <Label htmlFor="sds-sku" className="form-label text-sm">
                        SKU / Catalog Number
                      </Label>
                      <Input
                        id="sds-sku"
                        type="text"
                        placeholder="e.g., LC14950"
                        value={sdsSku}
                        onChange={(e) => setSdsSku(e.target.value)}
                        className="h-9"
                        data-testid="sds-sku-input"
                      />
                    </div>
                    {/* Spacer to match COA form height */}
                    <div className="h-[62px]"></div>
                    <Button
                      type="submit"
                      className="w-full h-10 bg-[#10B981] hover:bg-[#059669] text-white font-medium"
                      disabled={isSdsSearching}
                      data-testid="sds-search-button"
                    >
                      {isSdsSearching ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Searching...
                        </>
                      ) : (
                        <>
                          <Search className="mr-2 h-4 w-4" />
                          Search SDS
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Results and History */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            {/* Results Area */}
            <div className="lg:col-span-9">
              {/* Results Tabs */}
              {(coaResult || sdsResult) && (
                <div className="flex gap-2 mb-4">
                  <Button
                    variant={activeResultTab === "coa" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setActiveResultTab("coa")}
                    className={activeResultTab === "coa" ? "bg-[#4A7C9B] hover:bg-[#3D6680]" : ""}
                    disabled={!coaResult}
                    data-testid="coa-result-tab"
                  >
                    <FileText className="h-4 w-4 mr-1" />
                    COA Result
                  </Button>
                  <Button
                    variant={activeResultTab === "sds" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setActiveResultTab("sds")}
                    className={activeResultTab === "sds" ? "bg-[#10B981] hover:bg-[#059669]" : ""}
                    disabled={!sdsResult}
                    data-testid="sds-result-tab"
                  >
                    <ShieldCheck className="h-4 w-4 mr-1" />
                    SDS Result
                  </Button>
                </div>
              )}

              {/* COA Result */}
              {activeResultTab === "coa" && coaResult && (
                <div className="pdf-container fade-in border-2 border-[#4A7C9B] rounded-lg" data-testid="coa-result-container">
                  <div className="pdf-header bg-[#4A7C9B]/5">
                    <div className="pdf-info">
                      <span className="pdf-source text-[#4A7C9B]">{coaResult.source_name} - COA</span>
                      <span className="pdf-title">
                        SKU: {coaResult.sku} | Lot: {coaResult.lot_number}
                      </span>
                    </div>
                    <div className="pdf-actions">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => toggleFavorite(coaResult)}
                            className="border-[#E2E8F0]"
                            data-testid="coa-favorite-button"
                          >
                            {isFavorite(coaResult) ? (
                              <Star className="h-4 w-4 text-[#F59E0B] fill-[#F59E0B]" />
                            ) : (
                              <StarOff className="h-4 w-4 text-[#64748B]" />
                            )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          {isFavorite(coaResult) ? "Remove from favorites" : "Add to favorites"}
                        </TooltipContent>
                      </Tooltip>

                      {!coaResult.requires_manual_lookup && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openEmailDialog("coa", coaResult)}
                              className="border-[#E2E8F0]"
                              data-testid="coa-email-button"
                            >
                              <Mail className="h-4 w-4 text-[#64748B]" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Email COA link</TooltipContent>
                        </Tooltip>
                      )}

                      {coaResult.requires_manual_lookup ? (
                        <Button
                          size="sm"
                          onClick={() => window.open(coaResult.search_url, "_blank")}
                          className="bg-[#4A7C9B] hover:bg-[#3D6680] text-white"
                          data-testid="coa-open-search-button"
                        >
                          <ExternalLink className="h-4 w-4 mr-1" />
                          Open Search
                        </Button>
                      ) : (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(coaResult.coa_url, "_blank")}
                            className="border-[#E2E8F0]"
                            data-testid="coa-open-button"
                          >
                            <ExternalLink className="h-4 w-4 mr-1 text-[#64748B]" />
                            Open
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => window.open(coaResult.coa_url, "_blank")}
                            className="bg-[#4A7C9B] hover:bg-[#3D6680] text-white"
                            data-testid="coa-download-button"
                          >
                            <Download className="h-4 w-4 mr-1" />
                            Download
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {coaResult.requires_manual_lookup ? (
                    <div className="flex flex-col items-center justify-center py-12 px-8 bg-[#F8FAFC]">
                      <div className="w-14 h-14 bg-[#E2E8F0] rounded-full flex items-center justify-center mb-4">
                        <Search className="h-7 w-7 text-[#4A7C9B]" />
                      </div>
                      <h3 className="text-lg font-semibold text-[#1E293B] mb-2">Manual Lookup Required</h3>
                      <p className="text-[#64748B] text-center max-w-md mb-4 text-sm">
                        {coaResult.error || "Click the button above to search manually."}
                      </p>
                    </div>
                  ) : !getPdfViewerUrl(coaResult.coa_url, coaResult.source) ? (
                    <div className="flex flex-col items-center justify-center py-12 px-8 bg-[#F8FAFC]">
                      <div className="w-14 h-14 bg-[#E2E8F0] rounded-full flex items-center justify-center mb-4">
                        <FileText className="h-7 w-7 text-[#4A7C9B]" />
                      </div>
                      <h3 className="text-lg font-semibold text-[#1E293B] mb-2">Document Found</h3>
                      <p className="text-[#64748B] text-center max-w-md mb-4 text-sm">
                        Inline preview is not available for this supplier. Use the Open or Download buttons above to view the document.
                      </p>
                      <Button
                        onClick={() => window.open(coaResult.coa_url, "_blank")}
                        className="bg-[#4A7C9B] hover:bg-[#3D6680] text-white"
                        data-testid="coa-open-direct-button"
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Open Document
                      </Button>
                    </div>
                  ) : (
                    <div className="pdf-viewer-container">
                      {isCoaPdfLoading && (
                        <div className="flex items-center justify-center h-full bg-[#F1F5F9]">
                          <Loader2 className="h-8 w-8 animate-spin text-[#4A7C9B]" />
                        </div>
                      )}
                      <iframe
                        src={getPdfViewerUrl(coaResult.coa_url, coaResult.source)}
                        title="COA PDF Viewer"
                        className="pdf-frame"
                        onLoad={() => setIsCoaPdfLoading(false)}
                        onError={() => setIsCoaPdfLoading(false)}
                        style={{ display: isCoaPdfLoading ? "none" : "block" }}
                        data-testid="coa-pdf-iframe"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* SDS Result */}
              {activeResultTab === "sds" && sdsResult && (
                <div className="pdf-container fade-in border-2 border-[#10B981] rounded-lg" data-testid="sds-result-container">
                  <div className="pdf-header bg-[#10B981]/5">
                    <div className="pdf-info">
                      <span className="pdf-source text-[#10B981]">{sdsResult.source_name} - SDS</span>
                      <span className="pdf-title">
                        SKU: {sdsResult.sku}
                      </span>
                    </div>
                    <div className="pdf-actions">
                      {!sdsResult.requires_manual_lookup && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openEmailDialog("sds", sdsResult)}
                              className="border-[#E2E8F0]"
                              data-testid="sds-email-button"
                            >
                              <Mail className="h-4 w-4 text-[#64748B]" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Email SDS link</TooltipContent>
                        </Tooltip>
                      )}

                      {sdsResult.requires_manual_lookup ? (
                        <Button
                          size="sm"
                          onClick={() => window.open(sdsResult.search_url, "_blank")}
                          className="bg-[#10B981] hover:bg-[#059669] text-white"
                          data-testid="sds-open-search-button"
                        >
                          <ExternalLink className="h-4 w-4 mr-1" />
                          Open Search
                        </Button>
                      ) : (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(sdsResult.sds_url, "_blank")}
                            className="border-[#E2E8F0]"
                            data-testid="sds-open-button"
                          >
                            <ExternalLink className="h-4 w-4 mr-1 text-[#64748B]" />
                            Open
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => window.open(sdsResult.sds_url, "_blank")}
                            className="bg-[#10B981] hover:bg-[#059669] text-white"
                            data-testid="sds-download-button"
                          >
                            <Download className="h-4 w-4 mr-1" />
                            Download
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {sdsResult.requires_manual_lookup ? (
                    <div className="flex flex-col items-center justify-center py-12 px-8 bg-[#F8FAFC]">
                      <div className="w-14 h-14 bg-[#D1FAE5] rounded-full flex items-center justify-center mb-4">
                        <Search className="h-7 w-7 text-[#10B981]" />
                      </div>
                      <h3 className="text-lg font-semibold text-[#1E293B] mb-2">Manual Lookup Required</h3>
                      <p className="text-[#64748B] text-center max-w-md mb-4 text-sm">
                        {sdsResult.error || "Click the button above to search manually."}
                      </p>
                    </div>
                  ) : !getPdfViewerUrl(sdsResult.sds_url, sdsResult.source) ? (
                    <div className="flex flex-col items-center justify-center py-12 px-8 bg-[#F8FAFC]">
                      <div className="w-14 h-14 bg-[#D1FAE5] rounded-full flex items-center justify-center mb-4">
                        <ShieldCheck className="h-7 w-7 text-[#10B981]" />
                      </div>
                      <h3 className="text-lg font-semibold text-[#1E293B] mb-2">Document Found</h3>
                      <p className="text-[#64748B] text-center max-w-md mb-4 text-sm">
                        Inline preview is not available for this supplier. Use the Open or Download buttons above to view the document.
                      </p>
                      <Button
                        onClick={() => window.open(sdsResult.sds_url, "_blank")}
                        className="bg-[#10B981] hover:bg-[#059669] text-white"
                        data-testid="sds-open-direct-button"
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Open Document
                      </Button>
                    </div>
                  ) : (
                    <div className="pdf-viewer-container">
                      {isSdsPdfLoading && (
                        <div className="flex items-center justify-center h-full bg-[#F1F5F9]">
                          <Loader2 className="h-8 w-8 animate-spin text-[#10B981]" />
                        </div>
                      )}
                      <iframe
                        src={getPdfViewerUrl(sdsResult.sds_url, sdsResult.source)}
                        title="SDS PDF Viewer"
                        className="pdf-frame"
                        onLoad={() => setIsSdsPdfLoading(false)}
                        onError={() => setIsSdsPdfLoading(false)}
                        style={{ display: isSdsPdfLoading ? "none" : "block" }}
                        data-testid="sds-pdf-iframe"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* No Results State */}
              {!coaResult && !sdsResult && (
                <div className="no-results" data-testid="no-results">
                  <div className="no-results-icon">
                    <FileSearch className="h-10 w-10 text-[#94A3B8]" />
                  </div>
                  <h3>No Results Yet</h3>
                  <p>
                    Use the search forms above to find Certificates of Analysis (COA) or Safety Data Sheets (SDS).
                  </p>
                </div>
              )}
            </div>

            {/* History Sidebar */}
            <div className="lg:col-span-3" data-testid="history-sidebar">
              <Card className="history-sidebar">
                <div className="history-header">
                  <h3 className="flex items-center gap-2 text-[#1E293B] font-semibold text-sm">
                    <History className="h-4 w-4 text-[#4A7C9B]" />
                    Search History
                  </h3>
                </div>
                
                <ScrollArea className="h-[500px]">
                  {/* COA Favorites */}
                  {favorites.length > 0 && (
                    <div className="px-3 py-2 border-b border-[#E2E8F0]">
                      <span className="text-xs font-medium text-[#4A7C9B] uppercase tracking-wider">
                        COA Favorites
                      </span>
                      {favorites.map((item, index) => (
                        <div
                          key={`fav-${index}`}
                          className="history-item flex items-center justify-between py-2 px-1 cursor-pointer rounded hover:bg-[#F1F5F9]"
                          onClick={() => loadFromCoaHistory(item)}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1">
                              <Star className="h-3 w-3 text-[#F59E0B] fill-[#F59E0B] flex-shrink-0" />
                              <span className="history-item-source truncate text-xs">{item.source_name}</span>
                            </div>
                            <div className="history-item-sku truncate text-sm">{item.sku}</div>
                            <div className="history-item-lot truncate text-xs">Lot: {item.lot_number}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* COA History */}
                  {coaHistory.length > 0 && (
                    <div className="px-3 py-2 border-b border-[#E2E8F0]">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-[#4A7C9B] uppercase tracking-wider flex items-center gap-1">
                          <FileText className="h-3 w-3" />
                          COA History
                        </span>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                              <ChevronDown className="h-3 w-3" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={exportCoaToCSV}>
                              <FileDown className="h-3 w-3 mr-2" />
                              Export CSV
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={clearCoaHistory} className="text-red-600">
                              <Trash2 className="h-3 w-3 mr-2" />
                              Clear
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      {coaHistory.slice(0, 10).map((item) => (
                        <div
                          key={item.id}
                          className="history-item flex items-center justify-between py-1.5 px-1 cursor-pointer rounded hover:bg-[#F1F5F9]"
                          onClick={() => loadFromCoaHistory(item)}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="history-item-source truncate text-xs">{item.source_name}</div>
                            <div className="history-item-sku truncate text-sm">{item.sku}</div>
                            <div className="history-item-lot truncate text-xs">Lot: {item.lot_number}</div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-5 w-5 p-0 flex-shrink-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteCoaHistoryItem(item.id);
                            }}
                          >
                            <X className="h-3 w-3 text-[#CBD5E1]" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* SDS History */}
                  {sdsHistory.length > 0 && (
                    <div className="px-3 py-2">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-[#10B981] uppercase tracking-wider flex items-center gap-1">
                          <ShieldCheck className="h-3 w-3" />
                          SDS History
                        </span>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                              <ChevronDown className="h-3 w-3" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={exportSdsToCSV}>
                              <FileDown className="h-3 w-3 mr-2" />
                              Export CSV
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={clearSdsHistory} className="text-red-600">
                              <Trash2 className="h-3 w-3 mr-2" />
                              Clear
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      {sdsHistory.slice(0, 10).map((item) => (
                        <div
                          key={item.id}
                          className="history-item flex items-center justify-between py-1.5 px-1 cursor-pointer rounded hover:bg-[#D1FAE5]/30"
                          onClick={() => loadFromSdsHistory(item)}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="history-item-source truncate text-xs text-[#10B981]">{item.source_name}</div>
                            <div className="history-item-sku truncate text-sm">{item.sku}</div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-5 w-5 p-0 flex-shrink-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteSdsHistoryItem(item.id);
                            }}
                          >
                            <X className="h-3 w-3 text-[#CBD5E1]" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Empty State */}
                  {coaHistory.length === 0 && sdsHistory.length === 0 && favorites.length === 0 && (
                    <div className="py-8 text-center">
                      <History className="h-8 w-8 text-[#CBD5E1] mx-auto mb-2" />
                      <p className="text-sm text-[#94A3B8]">No search history yet</p>
                    </div>
                  )}
                </ScrollArea>
              </Card>
            </div>
          </div>
        </main>

        {/* Email Dialog */}
        <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
          <DialogContent className="sm:max-w-[425px]" data-testid="email-dialog">
            <DialogHeader>
              <DialogTitle>Email Document Link</DialogTitle>
              <DialogDescription>
                Send the {emailTarget?.type === "coa" ? "COA" : "SDS"} link for {emailTarget?.data?.sku} to an email address.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label htmlFor="email-input" className="text-sm font-medium">
                Email Address
              </Label>
              <Input
                id="email-input"
                type="email"
                placeholder="Enter email address"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
                className="mt-2"
                data-testid="email-input"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSendEmail}
                disabled={isSendingEmail}
                className="bg-[#4A7C9B] hover:bg-[#3D6680]"
              >
                {isSendingEmail ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="mr-2 h-4 w-4" />
                    Send
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}

export default App;
